/**
 * S-3053: Direct Cloudflare Upload Hook
 * 
 * Provides surgical upload flow:
 * 1. Get direct upload URL from edge function
 * 2. Upload file to Cloudflare
 * 3. Insert swing_runs record
 * 
 * Mirrors the proven test-s3041f3-e2e.sh flow
 * 
 * S-3053 FIX (Fixer):
 * Changed from PUT (raw binary) to POST (multipart form data)
 * This matches the working shell script and Cloudflare's expected format
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Standalone trigger for server-side orchestration (orchestrator-enqueue).
 * Use when you need to trigger orchestration outside of the useDirectUpload hook.
 * Do not call useDirectUpload() from callbacks or after await — use this function instead.
 *
 * Note: MVP upload path is client-side pose only; the server/GPU pose worker was deprecated.
 * This is kept for demo flows or other callers that still need server-side pose.
 */
export async function triggerOrchestrationForRun(runId: string): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured');
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('User not authenticated');
  }
  const response = await fetch(
    `${supabaseUrl}/functions/v1/orchestrator-enqueue`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ runId }),
    }
  );
  if (!response.ok) {
    const errorBody = await response.text();
    if (response.status === 409) {
      try {
        const errorData = JSON.parse(errorBody);
        if (errorData.code === 'ACTIVE_V3_PIPELINE_EXISTS') {
          throw new Error(errorData.message || 'Another video is still being analyzed. Please wait.');
        }
      } catch (e) {
        if (e instanceof Error && e.message.includes('still being analyzed')) throw e;
        throw new Error('Another video is still being analyzed. Please wait before uploading.');
      }
    }
    throw new Error(`Orchestration failed: ${response.status} ${errorBody}`);
  }
}

export type UploadProgress = { 
  pct: number; 
  bytesSent: number; 
  total: number 
};

export function useDirectUpload() {
  const controller = new AbortController();

  /**
   * Step 1: Get direct upload URL from edge function
   */
  async function getDirectUrl(authToken: string): Promise<{ uploadURL: string; uid: string }> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error("VITE_SUPABASE_URL not configured");
    }

    const url = `${supabaseUrl}/functions/v1/cloudflare-direct-upload`;
    
    // PLA-21: Log request details for debugging CORS/fetch issues
    console.log('[S-3053] Requesting direct upload URL:', {
      url,
      origin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
      hasAuthToken: !!authToken,
    });

    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${authToken}`, 
          "Content-Type": "application/json" 
        },
        signal: controller.signal,
      });
    } catch (fetchError) {
      // PLA-21: Enhanced error logging for "Failed to fetch" diagnosis
      console.error('[S-3053] Fetch failed:', {
        error: fetchError instanceof Error ? fetchError.message : String(fetchError),
        url,
        origin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
        hint: 'This may indicate a CORS configuration issue. Check that the origin is in ALLOWED_ORIGINS.',
      });
      throw fetchError;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get upload URL: ${errorText}`);
    }

    const data = await response.json();
    const { uploadURL, uid } = data;

    if (!uploadURL || !uid) {
      throw new Error("Invalid direct upload response: missing uploadURL or uid");
    }

    return { uploadURL, uid };
  }

  /**
   * Step 2: Upload file to Cloudflare with progress tracking
   * Uses XHR for progress events (fetch doesn't support upload progress)
   * 
   * S-3053 FIX: Use POST with multipart form data (matches test-s3041f3-e2e.sh)
   */
  async function uploadToCloudflare(
    file: File, 
    uploadURL: string, 
    onProgress?: (p: UploadProgress) => void
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Progress tracking
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable) {
          onProgress({ 
            pct: Math.round((e.loaded / e.total) * 100), 
            bytesSent: e.loaded, 
            total: e.total 
          });
        }
      };

      // Success
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('[S-3053] Cloudflare upload complete:', {
            status: xhr.status,
            statusText: xhr.statusText
          });
          resolve();
        } else {
          console.error('[S-3053] Cloudflare upload failed:', {
            status: xhr.status,
            statusText: xhr.statusText,
            response: xhr.responseText
          });
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };

      // Network error
      xhr.onerror = () => {
        console.error('[S-3053] XHR network error:', {
          readyState: xhr.readyState,
          status: xhr.status,
          uploadURL: uploadURL.substring(0, 50) + '...' // Log partial URL for debugging
        });
        reject(new Error("Network error during upload"));
      };

      // User cancelled
      xhr.onabort = () => {
        reject(new Error("Upload aborted"));
      };

      // Abort controller support
      controller.signal.addEventListener('abort', () => {
        xhr.abort();
      });

      // S-3053 FIX: Use POST with multipart form data (not PUT with raw binary)
      // This matches the proven working test-s3041f3-e2e.sh script
      const formData = new FormData();
      formData.append('file', file);

      xhr.open("POST", uploadURL, true);
      xhr.send(formData);
    });
  }

  /**
   * Step 3: Insert swing_runs record
   * Triggers orchestrator-consumer to enqueue pose → analyze
   * 
   * S-3053 FIX: Only insert columns that exist in schema
   * Schema has: id, user_id, status, cf_stream_uid, cf_playback_url, original_filename
   * Schema does NOT have: size_bytes, mime_type (removed)
   * 
   * S-3053 FIX #3: Must include user_id for RLS policy
   * RLS policy: auth.uid() = user_id (requires user_id in insert)
   */
  async function insertRun(uid: string, file: File, metadata?: Record<string, any>): Promise<string> {
    // S-3053 FIX #5: Use correct HLS manifest URL format (not watch.cloudflarestream.com)
    // Format: https://customer-{account}.cloudflarestream.com/{uid}/manifest/video.m3u8
    const playback = `https://customer-uh5f9h1v1dpl1ux6.cloudflarestream.com/${uid}/manifest/video.m3u8`;
    
    // Get current user for RLS
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not authenticated. Please log in and try again.");
    }
    
    const { data, error } = await supabase
      .from("swing_runs")
      .insert({
        user_id: user.id, // S-3053 FIX #3: Required for RLS policy
        status: "uploaded", // S-3054: Start as 'uploaded', orchestrator-enqueue will trigger processing
        cf_stream_uid: uid,
        cf_playback_url: playback,
        original_filename: file.name,
        analysis_stage: 'uploading', // Initial stage for Pizza Tracker
        analysis_progress: 0, // Initial progress
        metadata: metadata || {}, // Include optional metadata
        // S-3053 FIX: Removed size_bytes and mime_type (columns don't exist in schema)
      })
      .select("id")
      .single();

    if (error) {
      console.error('[S-3053] Database insert error:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw new Error(`Failed to create run: ${error.message}`);
    }

    if (!data?.id) {
      throw new Error("Failed to create run: no ID returned");
    }

    console.log('[S-3053] Database record created:', {
      runId: data.id,
      uid
    });

    return data.id as string;
  }

  /**
   * Step 4: Trigger orchestration (S-3054)
   * PLA-142: Now handles 409 ACTIVE_V3_PIPELINE_EXISTS response
   * 
   * Calls orchestrator-enqueue with user JWT to kick off pose extraction
   * 
   * This function:
   * - Accepts user auth token (safe for client-side)
   * - Server validates ownership
   * - PLA-142: Server checks for active pipelines (409 if exists)
   * - Waits 10s for Cloudflare HLS
   * - Triggers enqueue-pose-job server-to-server
   */
  async function triggerOrchestration(runId: string): Promise<void> {
    try {
      console.log('[S-3054] Triggering orchestration...', { runId });
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn('[S-3054] No session for orchestration trigger');
        throw new Error('User not authenticated');
      }

      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/orchestrator-enqueue`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ runId }),
        }
      );

      console.log('[S-3054] Orchestration response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error('[S-3054] Orchestration trigger failed:', {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });

        // PLA-142: Handle 409 Conflict - active pipeline exists
        if (response.status === 409) {
          try {
            const errorData = JSON.parse(errorBody);
            if (errorData.code === 'ACTIVE_V3_PIPELINE_EXISTS') {
              console.warn('[PLA-142] Concurrent upload blocked:', errorData);
              throw new Error(errorData.message || 'Another video is still being analyzed. Please wait.');
            }
          } catch (parseError) {
            // If parsing fails, use generic 409 message
            throw new Error('Another video is still being analyzed. Please wait before uploading.');
          }
        }

        throw new Error(`Orchestration failed: ${response.status} ${errorBody}`);
      }

      const result = await response.json();
      console.log('[S-3054] Orchestration triggered successfully:', result);
    } catch (error) {
      console.error('[S-3054] Error triggering orchestration:', error);
      throw error; // Propagate error to caller
    }
  }

  /**
   * Cancel ongoing upload
   */
  function cancel() { 
    controller.abort(); 
  }

  return { 
    getDirectUrl, 
    uploadToCloudflare, 
    insertRun,
    triggerOrchestration,
    cancel 
  };
}

